/**
 * Hand-written types for the admin property directory (list + detail).
 *
 * Matches the contracts in `docs/api-requirements.md` API-1.19 (list)
 * and API-1.20 (`GET /api/admin/properties/:id` detail). We don't
 * import the Orval-generated surface — see CLAUDE.md → TD-07. The
 * `PropertyStatus` enum mirrors the API's `schema.prisma`.
 */

export const PropertyStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  TRIAL: "TRIAL",
  PAUSED_NO_VENUES: "PAUSED_NO_VENUES",
  SUSPENDED: "SUSPENDED",
} as const;
export type PropertyStatus = (typeof PropertyStatus)[keyof typeof PropertyStatus];

/** `all` is a UI-only sentinel — never sent to the backend. */
export type PropertyStatusFilter = PropertyStatus | "all";

/** Row shape returned by `GET /api/admin/properties` (paginated list). */
export interface PropertyListItem {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly country: string;
  readonly status: PropertyStatus;
  readonly ownerId: string;
  /**
   * ADR-0009 prod test partition. Stamped from the owner's
   * `User.isTestAccount` AT CREATION and IMMUTABLE afterwards — there is no
   * edit path, by design: the append-only financial history behind an entity
   * can never be re-partitioned. Read-only in the backoffice; renders the
   * TEST badge and nothing else.
   *
   * The API declares this REQUIRED on the admin list + detail models (api
   * `1187c5b`). Optional here purely as a runtime guard against an API build
   * that predates ADR-0009, where the field is absent. An absent flag means
   * "real" — never "test".
   */
  readonly isTest?: boolean;
  readonly ownerEmail: string;
  readonly ownerName: string;
  readonly createdAt: string;
}

export interface PropertiesListFilters {
  readonly query: string;
  readonly status: PropertyStatusFilter;
  readonly page: number;
  readonly limit: number;
}

export interface PropertiesListResponse {
  readonly items: readonly PropertyListItem[];
  readonly totalCount: number;
  readonly page: number;
  readonly limit: number;
}

export interface PropertyOwnerSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
}

/** Platforms a property listing can be sourced from (API `AdminPropertyListingModel`). */
export const ListingPlatform = {
  AIRBNB: "AIRBNB",
  VRBO: "VRBO",
  BOOKING: "BOOKING",
} as const;
export type ListingPlatform = (typeof ListingPlatform)[keyof typeof ListingPlatform];

/** One external listing attached to a property (admin detail). */
export interface PropertyListing {
  readonly id: string;
  /** May be a value outside the known enum if the API adds platforms later. */
  readonly platform: ListingPlatform | string;
  readonly externalListingId: string;
}

/** Detail shape returned by `GET /api/admin/properties/:id` (API-1.20). */
export interface PropertyDetail {
  readonly id: string;
  /**
   * ADR-0009 prod test partition. Stamped from the owner's
   * `User.isTestAccount` AT CREATION and IMMUTABLE afterwards — there is no
   * edit path, by design: the append-only financial history behind an entity
   * can never be re-partitioned. Read-only in the backoffice; renders the
   * TEST badge and nothing else.
   *
   * The API declares this REQUIRED on the admin list + detail models (api
   * `1187c5b`). Optional here purely as a runtime guard against an API build
   * that predates ADR-0009, where the field is absent. An absent flag means
   * "real" — never "test".
   */
  readonly isTest?: boolean;
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
  readonly country: string;
  readonly latitude: number;
  readonly longitude: number;
  /** In **miles** — matches the API's `Property.geofenceRadius Float // miles`. */
  readonly geofenceRadius: number;
  /**
   * Capacity pivot: max guests the listing sleeps. Recommendation input only —
   * does not gate the tier or the pass quota. Editable by admin + support.
   */
  readonly maxGuests: number;
  /**
   * Capacity pivot: max guest-slots issued per day (enforced at pass approval).
   * Seeded from the active tier, re-seeded on plan change; editable by admin + support.
   */
  readonly dailyPassCap: number;
  readonly description?: string | null;
  readonly photos: readonly string[];
  readonly timezone: string;
  readonly status: PropertyStatus;
  /** Active subscription tier key (absent when the property has no subscription). */
  readonly subscriptionTier?: string | null;
  /** Active subscription status (absent when the property has no subscription). */
  readonly subscriptionStatus?: string | null;
  readonly listings: readonly PropertyListing[];
  readonly owner: PropertyOwnerSummary;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Body for `PATCH /api/admin/properties/:id`. All fields optional; only the
 * dirty ones are sent. No per-field RBAC: ADMIN + SUPPORT may both edit every
 * field (the API `@Roles(ADMIN, SUPPORT)` is the gate); the API is authoritative.
 */
export interface UpdatePropertyInput {
  readonly maxGuests?: number;
  readonly dailyPassCap?: number;
  /** Coverage radius in MILES (1–20). Owners cannot edit it — backoffice-only. */
  readonly geofenceRadius?: number;
  // `country` is deliberately absent: it is fixed at creation and the API rejects changes
  // (PROPERTY_COUNTRY_IMMUTABLE). It stays readable on `PropertyDetail`.
}
